import { TableContainer, Paper, Table, TableRow, TableCell, TableBody, TablePagination } from "@mui/material";
import { DataExtractionRowViewModel } from "./ViewModels/DataExtractionTableRow";
import ExtractionTablHeader from "./ExtractionTableHeader";
import { useMemo, useState } from "react";
import TableOptions from "./TableHeadOptions";

interface IProps {
    rows: DataExtractionRowViewModel[];
    landUse: string;
    setLandUse: (landUse: string) => void;
}

export default function TravlDataExtractionTable({ rows, landUse, setLandUse }: IProps) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selectedRow, setSelectedRow] = useState('');
    const [action, setAction] = useState("all");

    const keys = useMemo(
        () => Object.keys(rows[0]) as (keyof DataExtractionRowViewModel)[],
        [rows]
    );

    const hasNonZeroOrNonEmpty = useMemo(
        () =>
            keys.map(key =>
                rows.some(row => {
                    const value = row[key];
                    const num = Number(value);
                    if (!isNaN(num) && value.trim() !== "") return num !== 0;
                    return value.trim() !== "";
                })
            ),
        [rows, keys]
    );

    const visibleKeys = useMemo(
        () => keys.filter((_, idx) => hasNonZeroOrNonEmpty[idx]),
        [keys, hasNonZeroOrNonEmpty]
    );

    const pagedRows = useMemo(
        () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [rows, page, rowsPerPage]
    );

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <TableOptions landUse={landUse} setLandUse={(s: string) => { setLandUse(s); setPage(0); }} rows={rows} rowKeys={visibleKeys} action={action} setAction={setAction} selectedRowCode={selectedRow} />
            <TableContainer component={Paper}>
                <Table size={'small'} sx={{
                    'td, th': {
                        padding: '4px',
                        fontSize: '0.73rem',
                        whiteSpace: 'nowrap',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }
                }}>
                    <ExtractionTablHeader tableHeaders={visibleKeys} />
                    <TableBody>
                        {pagedRows.map((row, idx) => {
                            const isSelectable = action === "highlighted";
                            const isSelected = isSelectable && selectedRow === row.surveyCode;
                            return (
                                <TableRow
                                    key={row.surveyCode + idx}
                                    hover={isSelectable}
                                    selected={isSelected}
                                    onClick={isSelectable ? () => setSelectedRow(row.surveyCode) : undefined}
                                    sx={{
                                        cursor: isSelectable ? "pointer" : "default",
                                        '&.Mui-selected, &.Mui-selected:hover': isSelectable
                                            ? { backgroundColor: '#ffef96 !important' }
                                            : {},
                                    }}
                                >
                                    {visibleKeys.map((key) => (
                                        <TableCell key={String(key)}>
                                            {row[key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {/* empty rows */}
                        {Array.from({ length: Math.max(0, rowsPerPage - pagedRows.length) }).map((_, i) => (
                            <TableRow key={`empty-${i}`} sx={{ height: 25.7 }} > {/* need to unhard code this */}
                                {visibleKeys.map((key, colIdx) => (
                                    <TableCell key={String(key) + colIdx} />
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TablePagination
                                count={rows.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

